/**
 * MSW Overrides: Billing Receipts scenario
 *
 * Returns mock receipt data for the billing receipts table so the
 * BillingReceiptsSection can be tested in local development.
 *
 * Enable by setting ENABLE_MSW=true and MSW_OVERRIDE=true in your .env,
 * then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse } from 'msw';
import { BFF_ENDPOINTS, toMSWPattern, createMockReceipts } from '@cvo/domain';
import type { ReceiptsListResponse, ReceiptResponse } from '@cvo/domain';

const mockReceipts = createMockReceipts();

export function createBillingReceiptsOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  return [
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.RECEIPTS)}`,
      ({ request }) => {
        const url = new URL(request.url);
        const page = Number(url.searchParams.get('page') ?? 0);
        const size = Number(url.searchParams.get('size') ?? mockReceipts.length);
        const start = page * size;
        const content = mockReceipts.slice(start, start + size);
        const totalPages = Math.ceil(mockReceipts.length / size);

        const response: ReceiptsListResponse = {
          content,
          page: {
            size,
            number: page,
            totalElements: mockReceipts.length,
            totalPages,
          },
        };

        return HttpResponse.json(response);
      },
    ),

    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.RECEIPT)}`,
      ({ params }) => {
        const receipt: ReceiptResponse =
          mockReceipts.find(receiptMock => receiptMock.id === params.receiptId) ?? mockReceipts[0]!;
        return HttpResponse.json(receipt);
      },
    ),
  ];
}
